import { useState, useCallback } from "react";
import {
  carbonProjects,
  treeCarbonEstimates,
  carbonAreas,
  carbonCompensations,
  carbonMethodologies,
  carbonReports,
} from "@/mocks/carbono";
import type { CarbonProject } from "@/mocks/carbono";
import CarbonKpiCard from "./components/CarbonKpiCard";
import CarbonOverview from "./components/CarbonOverview";
import CarbonProjectCard from "./components/CarbonProjectCard";
import CarbonProjectTable from "./components/CarbonProjectTable";
import TreeCarbonTable from "./components/TreeCarbonTable";
import CarbonAreaCard from "./components/CarbonAreaCard";
import CompensationTable from "./components/CompensationTable";
import MethodologyCard from "./components/MethodologyCard";
import CarbonReportsGrid from "./components/CarbonReportsGrid";
import CarbonProjectDetailDrawer from "./components/CarbonProjectDetailDrawer";

const tabs = [
  { id: "overview", label: "Visão Geral" },
  { id: "projects", label: "Projetos de Carbono" },
  { id: "trees", label: "Estimativas por Árvore" },
  { id: "areas", label: "Áreas e UCs" },
  { id: "compensations", label: "Compensações" },
  { id: "methodologies", label: "Metodologias" },
  { id: "reports", label: "Relatórios" },
];

export default function Carbono() {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedProject, setSelectedProject] = useState<CarbonProject | null>(null);
  const [showTable, setShowTable] = useState(false);

  const handleOpenProject = useCallback((project: CarbonProject) => {
    setSelectedProject(project);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedProject(null);
  }, []);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="text-xs text-foreground-500 mb-1">Dashboard &gt; Carbono</div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
            Carbono e Ativos Ambientais
          </h1>
          <p className="text-sm text-foreground-500 mt-1 max-w-2xl">
            Estimativas preliminares de biomassa, carbono, CO2e e ativos ambientais vinculados ao CTA, áreas verdes e projetos urbanos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-add-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Novo projeto
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap">
            <i className="ri-refresh-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Atualizar
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-download-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Exportar
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
            <i className="ri-file-chart-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Relatório
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap">
            <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Metodologia
          </button>
        </div>
      </div>

      {/* Technical Warning */}
      <div className="bg-caution-50 border border-caution-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-caution-100 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-caution-700 w-full h-full flex items-center justify-center text-lg" />
          </div>
          <div>
            <div className="text-sm font-semibold text-caution-800 mb-1">Aviso técnico — estimativas preliminares</div>
            <p className="text-xs text-caution-700 leading-relaxed">
              Os valores apresentados são estimativas preliminares baseadas em dados dendrométricos, espécies cadastradas e modelos simplificados. Não representam certificação oficial de crédito de carbono. As metodologias devem ser calibradas por responsável técnico conforme espécie, região e objetivo do projeto.
            </p>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 md:gap-4">
        <CarbonKpiCard label="CO2e estimado total" value="4.280 t" icon="ri-cloud-line" color="primary" subtitle="Acumulado preliminar" />
        <CarbonKpiCard label="Carbono estimado" value="1.167 t C" icon="ri-leaf-line" color="accent" subtitle="Armazenamento estimado" />
        <CarbonKpiCard label="Biomassa estimada" value="2.914 t" icon="ri-tree-line" color="secondary" subtitle="Massa seca estimada" />
        <CarbonKpiCard label="Árvores consideradas" value="10.932" icon="ri-plant-line" color="primary" subtitle="Com dados para estimativa" />
        <CarbonKpiCard label="Áreas verdes vinculadas" value="64" icon="ri-landscape-line" color="accent" subtitle="Praças, parques, APPs" />
        <CarbonKpiCard label="UCs vinculadas" value="9" icon="ri-shield-line" color="secondary" subtitle="Unidades de Conservação" />
        <CarbonKpiCard label="Projetos de carbono" value="6" icon="ri-folder-chart-line" color="primary" subtitle="Ativos e monitorados" />
        <CarbonKpiCard label="Nível médio de confiança" value="72%" icon="ri-bar-chart-grouped-line" color="accent" subtitle="Média dos projetos" />
      </div>

      {/* Tabs */}
      <div className="border-b border-background-200">
        <div className="flex gap-1 overflow-x-auto pb-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap rounded-t-lg transition-colors border-b-2 ${
                activeTab === tab.id
                  ? "text-primary-700 border-primary-500 bg-primary-50/50"
                  : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === "overview" && <CarbonOverview />}

        {activeTab === "projects" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground-900">Projetos de Carbono</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowTable(false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!showTable ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-grid-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Cards
                </button>
                <button
                  onClick={() => setShowTable(true)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${showTable ? "text-white bg-primary-500" : "text-foreground-600 bg-background-50 hover:bg-background-100"}`}
                >
                  <i className="ri-table-line w-3.5 h-3.5 inline-flex items-center justify-center mr-1" />
                  Tabela
                </button>
              </div>
            </div>
            {showTable ? (
              <CarbonProjectTable projects={carbonProjects} onOpen={handleOpenProject} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {carbonProjects.map((project) => (
                  <CarbonProjectCard key={project.id} project={project} onOpen={handleOpenProject} />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "trees" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground-900">Estimativas por Árvore</h3>
            <TreeCarbonTable estimates={treeCarbonEstimates} />
          </div>
        )}

        {activeTab === "areas" && (
          <div className="space-y-5">
            <h3 className="text-sm font-semibold text-foreground-900">Áreas e Unidades de Conservação</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {carbonAreas.map((area) => (
                <CarbonAreaCard key={area.id} area={area} />
              ))}
            </div>
          </div>
        )}

        {activeTab === "compensations" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground-900">Compensações Ambientais</h3>
            <CompensationTable compensations={carbonCompensations} />
          </div>
        )}

        {activeTab === "methodologies" && (
          <div className="space-y-5">
            <div className="bg-accent-50 border border-accent-100 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <i className="ri-book-open-line text-accent-600 w-5 h-5 flex items-center justify-center shrink-0 mt-0.5" />
                <p className="text-xs text-accent-700 leading-relaxed">
                  As metodologias exibidas são referenciais e devem ser calibradas por responsável técnico conforme espécie, região, literatura científica e objetivo do projeto. A precisão varia conforme a qualidade dos dados de entrada.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {carbonMethodologies.map((m) => (
                <MethodologyCard key={m.id} methodology={m} />
              ))}
            </div>
          </div>
        )}

        {activeTab === "reports" && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-foreground-900">Relatórios Técnicos</h3>
            <CarbonReportsGrid reports={carbonReports} />
          </div>
        )}
      </div>

      {/* Drawer */}
      {selectedProject && (
        <CarbonProjectDetailDrawer project={selectedProject} onClose={handleCloseDrawer} />
      )}
    </div>
  );
}